/**
 * 管理员系统模型管理页面
 */
import React, { useState, useEffect } from 'react';
import { adminApi, SystemModelInfo } from '../../services/adminApi';
import api from '../../services/api';
import { confirm } from '../../store/toastStore';
import { LightBulbIcon } from '@heroicons/react/24/outline';

const AdminModels: React.FC = () => {
  const [models, setModels] = useState<SystemModelInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<SystemModelInfo | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [testingId, setTestingId] = useState<number | null>(null);
  const [testResult, setTestResult] = useState<{ id: number; success: boolean; message: string; responseTime?: number } | null>(null);
  const [allUsers, setAllUsers] = useState<{ id: number; username: string; email: string }[]>([]);
  const [userSearch, setUserSearch] = useState('');

  const [form, setForm] = useState({
    name: '', provider: 'deepseek', model_name: '', base_url: '',
    api_key: '', is_active: true, is_default: false, sort_order: 0,
    max_concurrent_requests: 3, description: '',
    visibility: 'all' as string,
    visible_users: [] as number[],
    display_name: '',
  });

  const loadModels = async () => {
    setLoading(true);
    try { const res = await adminApi.getSystemModels(); setModels(res.data); }
    catch { setError('加载失败'); }
    finally { setLoading(false); }
  };

  useEffect(() => { loadModels(); }, []);

  const loadUsers = async () => {
    try {
      const res = await api.get('/api/admin/users', { params: { page_size: 500 } });
      setAllUsers(res.data.users || res.data.items || []);
    } catch { /* ignore */ }
  };

  const resetForm = () => {
    setForm({ name: '', provider: 'deepseek', model_name: '', base_url: '', api_key: '', is_active: true, is_default: false, sort_order: 0, max_concurrent_requests: 3, description: '', visibility: 'all', visible_users: [], display_name: '' });
    setEditing(null); setShowCreate(false); setError('');
  };

  const handleEdit = (m: SystemModelInfo) => {
    setEditing(m);
    setForm({ name: m.name, provider: m.provider, model_name: m.model_name, base_url: m.base_url || '', api_key: '', is_active: m.is_active, is_default: m.is_default, sort_order: m.sort_order, max_concurrent_requests: m.max_concurrent_requests, description: m.description || '', visibility: m.visibility || 'all', visible_users: m.visible_users || [], display_name: m.display_name || '' });
    setShowCreate(false); setError('');
    loadUsers();
  };

  const handleSave = async () => {
    if (!form.name || !form.model_name) { setError('请填写名称和模型名'); return; }
    if (!editing && !form.api_key) { setError('请填写 API Key'); return; }
    setSaving(true); setError('');
    try {
      const data: any = { ...form };
      if (editing && !data.api_key) delete data.api_key;
      if (editing) await adminApi.updateSystemModel(editing.id, data);
      else await adminApi.createSystemModel(data);
      resetForm(); await loadModels();
    } catch (err: any) { setError(err.response?.data?.detail || '保存失败'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (m: SystemModelInfo) => {
    if (!await confirm({ title: '确认删除', message: `确定删除「${m.name}」吗？所有用户的该模型引用将被禁用。`, confirmLabel: '删除', danger: true })) return;
    try { await adminApi.deleteSystemModel(m.id); loadModels(); }
    catch (err: any) { setError(err.response?.data?.detail || '删除失败'); }
  };

  const handleTest = async (id: number) => {
    setTestingId(id); setTestResult(null);
    try {
      const response = await api.post(`/api/admin/models/${id}/test`);
      setTestResult({ id, success: response.data.success, message: response.data.message, responseTime: response.data.response_time });
    } catch (err: any) {
      setTestResult({ id, success: false, message: err.response?.data?.detail || '测试失败' });
    } finally { setTestingId(null); }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-xl font-bold text-foreground">模型管理</h1>
          <p className="text-xs text-muted-foreground mt-1">管理系统模型，修改 Key 即时全局生效</p>
        </div>
        <button onClick={() => { resetForm(); setShowCreate(true); loadUsers(); }} className="px-4 py-2 bg-primary hover:bg-primary/80 text-white text-sm font-semibold rounded-xl">
          + 新建模型
        </button>
      </div>

      {error && <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700">{error}</div>}

      {/* 表单弹窗 */}
      {(showCreate || editing) && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50" onClick={resetForm} />
          <div className="relative bg-card rounded-2xl border border-border shadow-[0_20px_60px_rgba(0,0,0,0.15)] w-full max-w-lg p-6 max-h-[90vh] overflow-y-auto">
            <h2 className="text-lg font-bold text-foreground mb-4">{editing ? '编辑模型' : '新建系统模型'}</h2>
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-muted-foreground mb-1">名称</label>
                  <input value={form.name} onChange={e => setForm({...form, name: e.target.value})} className="w-full px-3 py-2 border border-border rounded-xl text-sm focus:outline-none focus:border-primary" placeholder="DeepSeek V3" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-muted-foreground mb-1">提供商</label>
                  <select value={form.provider} onChange={e => setForm({...form, provider: e.target.value})} className="w-full px-3 py-2 border border-border rounded-xl text-sm focus:outline-none focus:border-primary">
                    <option value="deepseek">DeepSeek</option><option value="openai">OpenAI</option><option value="claude">Claude</option><option value="custom">自定义</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-muted-foreground mb-1">模型名</label>
                <input value={form.model_name} onChange={e => setForm({...form, model_name: e.target.value})} className="w-full px-3 py-2 border border-border rounded-xl text-sm focus:outline-none focus:border-primary" placeholder="deepseek-chat" />
              </div>
              <div>
                <label className="block text-xs font-medium text-muted-foreground mb-1">基础地址</label>
                <input value={form.base_url} onChange={e => setForm({...form, base_url: e.target.value})} className="w-full px-3 py-2 border border-border rounded-xl text-sm focus:outline-none focus:border-primary" placeholder="https://api.deepseek.com/v1" />
              </div>
              <div>
                <label className="block text-xs font-medium text-muted-foreground mb-1">API Key {editing && <span className="text-muted-foreground">(留空不修改)</span>}</label>
                <input type="password" value={form.api_key} onChange={e => setForm({...form, api_key: e.target.value})} className="w-full px-3 py-2 border border-border rounded-xl text-sm focus:outline-none focus:border-primary" placeholder={editing ? '留空则不修改' : 'sk-...'} />
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="block text-xs font-medium text-muted-foreground mb-1">并发数</label>
                  <input type="number" value={form.max_concurrent_requests} onChange={e => setForm({...form, max_concurrent_requests: parseInt(e.target.value)||3})} className="w-full px-3 py-2 border border-border rounded-xl text-sm focus:outline-none focus:border-primary" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-muted-foreground mb-1">排序</label>
                  <input type="number" value={form.sort_order} onChange={e => setForm({...form, sort_order: parseInt(e.target.value)||0})} className="w-full px-3 py-2 border border-border rounded-xl text-sm focus:outline-none focus:border-primary" />
                </div>
                <div className="flex items-end gap-3 pb-2">
                  <label className="flex items-center gap-1.5 text-sm text-foreground"><input type="checkbox" checked={form.is_active} onChange={e => setForm({...form, is_active: e.target.checked})} className="w-4 h-4 text-primary" />启用</label>
                  <label className="flex items-center gap-1.5 text-sm text-foreground"><input type="checkbox" checked={form.is_default} onChange={e => setForm({...form, is_default: e.target.checked})} className="w-4 h-4 text-primary" />默认</label>
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-muted-foreground mb-1">描述</label>
                <input value={form.description} onChange={e => setForm({...form, description: e.target.value})} className="w-full px-3 py-2 border border-border rounded-xl text-sm focus:outline-none focus:border-primary" />
              </div>
              {/* visibility */}
              <div>
                <label className="block text-xs font-medium text-muted-foreground mb-1">可见范围</label>
                <select value={form.visibility} onChange={e => setForm({...form, visibility: e.target.value})} className="w-full px-3 py-2 border border-border rounded-xl text-sm">
                  <option value="all">全部用户</option>
                  <option value="specific">指定用户</option>
                </select>
              </div>
              {form.visibility === 'specific' && (
                <div>
                  <label className="block text-xs font-medium text-muted-foreground mb-1">指定用户</label>
                  <input value={userSearch} onChange={e => setUserSearch(e.target.value)} placeholder="搜索用户..." className="w-full px-3 py-2 border border-border rounded-xl text-sm mb-2" />
                  <div className="max-h-32 overflow-y-auto space-y-1 border border-border rounded-xl p-2">
                    {allUsers.filter(u => !userSearch || u.username.includes(userSearch) || u.email.includes(userSearch)).map(u => (
                      <label key={u.id} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-accent rounded p-1">
                        <input type="checkbox" checked={form.visible_users.includes(u.id)} onChange={() => {
                          setForm(prev => ({
                            ...prev,
                            visible_users: prev.visible_users.includes(u.id)
                              ? prev.visible_users.filter(id => id !== u.id)
                              : [...prev.visible_users, u.id]
                          }));
                        }} className="w-4 h-4" />
                        {u.username} ({u.email})
                      </label>
                    ))}
                  </div>
                </div>
              )}
              {form.visibility === 'all' && (
                <div>
                  <label className="block text-xs font-medium text-muted-foreground mb-1">用户端展示名称</label>
                  <input value={form.display_name} onChange={e => setForm({...form, display_name: e.target.value})} className="w-full px-3 py-2 border border-border rounded-xl text-sm" placeholder="留空使用模型名" />
                </div>
              )}
              <div className="p-3 bg-primary/10 border border-primary/20 rounded-xl text-xs text-primary">
                <LightBulbIcon className="w-5 h-5 inline-block" /> {editing ? '修改 Key 将立即对所有使用此模型的用户生效。' : (form.visibility === 'all' ? '创建后全部用户可见。' : `创建后仅指定用户可见。`)}
              </div>
            </div>
            <div className="mt-6 flex justify-end gap-3">
              <button onClick={resetForm} className="px-4 py-2 text-sm text-foreground bg-accent rounded-xl hover:bg-accent">取消</button>
              <button onClick={handleSave} disabled={saving} className="px-4 py-2 bg-primary hover:bg-primary/80 text-white text-sm font-semibold rounded-xl disabled:opacity-50">{saving ? '保存中...' : '保存'}</button>
            </div>
          </div>
        </div>
      )}

      {/* 模型列表 —— 卡片风格 */}
      {loading ? (
        <div className="flex justify-center items-center py-24">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
          <span className="ml-3 text-sm text-muted-foreground">读取模型数据中...</span>
        </div>
      ) : models.length === 0 ? (
        <div className="text-center py-20 bg-card rounded-2xl border border-border px-4">
          <p className="text-sm text-muted-foreground mb-4">暂无系统模型</p>
          <button onClick={() => setShowCreate(true)} className="px-4 py-2 border border-border text-sm font-semibold rounded-xl text-primary bg-card hover:bg-accent">创建第一个系统模型</button>
        </div>
      ) : (
        <div className="space-y-4">
          {models.map((model) => (
            <div
              key={model.id}
              className="bg-card rounded-2xl border border-border p-6 shadow-[0_12px_33px_0_rgba(0,0,0,0.015)] hover:border-primary/10 hover:shadow-[0_15px_35px_rgba(0,0,0,0.03)] transition-all duration-300 flex flex-col md:flex-row justify-between items-start md:items-center gap-6"
            >
              <div className="flex-1">
                <div className="flex items-center space-x-2.5 mb-2">
                  <h3 className="text-base font-bold text-foreground">{model.name}</h3>
                  <span className="px-2 py-0.5 text-[9px] font-semibold text-muted-foreground bg-accent border border-border/50 rounded-lg">{model.provider.toUpperCase()}</span>
                  <span className="px-2 py-0.5 text-[9px] font-mono text-muted-foreground bg-accent border border-border/50 rounded-lg">{model.model_name}</span>
                  {model.is_active ? (
                    <span className="px-2.5 py-0.5 text-[9px] font-bold bg-green-50 text-green-700 border border-green-200/80 rounded-lg">启用</span>
                  ) : (
                    <span className="px-2.5 py-0.5 text-[9px] font-bold bg-accent text-muted-foreground border border-border/80 rounded-lg">禁用</span>
                  )}
                  {model.is_default && (
                    <span className="px-2.5 py-0.5 text-[9px] font-bold bg-primary/10 text-primary border border-primary/20 rounded-lg">★ 默认</span>
                  )}
                  {model.visibility === 'all' ? (
                    <span className="px-2.5 py-0.5 text-[9px] font-bold bg-blue-50 text-blue-700 border border-blue-200/80 rounded-lg">公开</span>
                  ) : (
                    <span className="px-2.5 py-0.5 text-[9px] font-bold bg-amber-50 text-amber-700 border border-amber-200/80 rounded-lg">{(model.visible_users?.length || 0)} 个用户</span>
                  )}
                  {model.display_name && (
                    <span className="px-2 py-0.5 text-[9px] text-muted-foreground bg-accent rounded-lg">{model.display_name}</span>
                  )}
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1.5 mt-3.5 text-xs text-muted-foreground">
                  {model.base_url && (
                    <div className="flex items-center truncate" title={model.base_url}>
                      <span className="font-semibold text-foreground mr-1.5">接入端点:</span>
                      <span className="font-mono text-[11px] truncate">{model.base_url}</span>
                    </div>
                  )}
                  <div className="flex items-center">
                    <span className="font-semibold text-foreground mr-1.5">并发控制:</span>
                    <span>最大 <span className="font-semibold text-foreground">{model.max_concurrent_requests}</span> 个并发请求</span>
                  </div>
                  <div className="flex items-center">
                    <span className="font-semibold text-foreground mr-1.5">创建时间:</span>
                    <span>{new Date(model.created_at).toLocaleDateString()}</span>
                  </div>
                  {model.description && (
                    <div className="flex items-center col-span-2">
                      <span className="font-semibold text-foreground mr-1.5">描述:</span>
                      <span>{model.description}</span>
                    </div>
                  )}
                </div>

                {testResult && testResult.id === model.id && (
                  <div className={`mt-4 p-3.5 rounded-xl text-xs flex items-start gap-2.5 ${testResult.success ? 'bg-[#eafaf1] border border-[#d1f2e1]/80 text-[#18a058]' : 'bg-red-50 border border-red-200/50 text-red-800'}`}>
                    <span className="text-sm">{testResult.success ? '✓' : '✗'}</span>
                    <div className="flex-1">
                      <p className="font-bold">{testResult.success ? '连接测试通过' : '连接测试失败'}</p>
                      <p className="mt-1.5 leading-relaxed text-muted-foreground">{testResult.message}</p>
                      {testResult.responseTime && (
                        <p className="mt-2 text-[10px] opacity-75 font-semibold">响应时延: <span className="font-mono">{testResult.responseTime}s</span></p>
                      )}
                    </div>
                  </div>
                )}
              </div>

              <div className="flex flex-row md:flex-col md:space-y-2.5 space-x-3 md:space-x-0 w-full md:w-auto pt-4 md:pt-0 border-t md:border-t-0 border-border/80 justify-end">
                <button
                  onClick={() => handleTest(model.id)}
                  disabled={testingId === model.id}
                  className={`text-xs font-bold px-3 py-2 rounded-xl transition-all ${testingId === model.id ? 'bg-accent text-muted-foreground cursor-not-allowed border border-border' : 'text-primary bg-card border border-border hover:bg-primary/10 hover:border-primary/20'}`}
                >
                  {testingId === model.id ? '正在连接...' : '测试连接'}
                </button>
                <button onClick={() => handleEdit(model)} className="text-xs font-bold px-3 py-2 text-[#18a058] bg-card border border-border hover:bg-[#eafaf1]/40 rounded-xl transition-all">编辑</button>
                <button onClick={() => handleDelete(model)} className="text-xs font-bold px-3 py-2 text-red-500 bg-card border border-border hover:bg-red-50 rounded-xl transition-all">删除</button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminModels;
